// GlobalCreateButton — «+» flotante de CREAR (convive lado a lado con el FAB dorado «Agenda»).
// El menú depende del rol de vista («Ver como»): ver createMenu.js. Los atajos 🔒 salen como «Próximamente».

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, X, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { FeedPostUploader } from '@/components/Media/FeedPostUploader';
import { UploadContentDialog } from '@/components/Upload/UploadContentDialog';
import { StoryUploadDialog } from '@/components/Stories/StoryUploadDialog';
import { HighlightEditor } from '@/components/Profile/HighlightEditor';
import { RequestReviewDialog } from '@/components/Reviews/RequestReviewDialog';
import { communityChallengesApi } from '@/lib/communityChallengesApi';
import { toast } from 'sonner';
import { usePwaInstall } from '@/pwa/PwaInstallContext';
import { PWA_BANNER_LIFT_PX, AGENDA_FAB_WIDTH_PX, FAB_GAP_PX, AGENDA_FAB_EVENT } from '@/components/Agenda/AgendaFab';
import { buildCreateSections, CREATE_FAB_ROLES } from './createMenu';
import { tr } from '@/contexts/LanguageContext';

// Rutas con su propia barra inferior / CTA pegajoso donde el FAB estorba.
const HIDDEN_ROUTE_PREFIXES = [
  '/course/', '/product/', '/auth', '/professional', '/settings', '/search',
  '/tutorial/', '/learn', '/liked', '/apply-model', '/applications', '/support', '/judge-training',
  '/tournament/', '/post/', '/posts/', '/messages', '/seller/', '/admin', '/legal', '/pay/', '/qr/', '/resena/', '/checkout', '/live/', '/judge/onboarding',
];

export const GlobalCreateButton = () => {
  const { user } = useAuth();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  const location = useLocation();
  const { bannerVisible } = usePwaInstall();
  const [open, setOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);
  const [reviewLinkOpen, setReviewLinkOpen] = useState(false);
  const [feedDestination, setFeedDestination] = useState(null);
  const [agendaFab, setAgendaFab] = useState(false);

  useEffect(() => {
    const h = (e) => setAgendaFab(Boolean(e.detail?.visible));
    window.addEventListener(AGENDA_FAB_EVENT, h);
    return () => window.removeEventListener(AGENDA_FAB_EVENT, h);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.classList.toggle('lbw-create-open', open);
    return () => document.body.classList.remove('lbw-create-open');
  }, [open]);

  if (!user || !CREATE_FAB_ROLES.has(user.role)) return null;
  if (location.pathname === '/') return null;
  if (HIDDEN_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;
  if (location.pathname.endsWith('/reservar') || location.pathname.startsWith('/reservas')) return null;

  const close = () => setOpen(false);
  const actions = {
    go: (path) => { close(); navigate(path); },
    openPost: () => { close(); setPostOpen(true); },
    openStory: () => { close(); setStoryOpen(true); },
    openHighlight: () => { close(); setHighlightOpen(true); },
    openReviewLink: () => { close(); setReviewLinkOpen(true); },
    openFeed: (dest) => { close(); setFeedDestination(dest); },
    voteChallenge: async () => {
      close();
      try {
        const r = await communityChallengesApi.active();
        if (r?.challenge?.id) navigate(`/reto/${r.challenge.id}`);
        else toast.info(tr("No hay retos activos ahora mismo."));
      } catch { toast.error(tr("No se pudo cargar el reto activo.")); }
    },
  };
  const sections = buildCreateSections({ role: user.role, identity: user.real_role || user.role, isBarber: Boolean(user.is_barber), flags }, actions)
    .filter((s) => s.items.length > 0);
  if (sections.length === 0) return null;

  const lift = bannerVisible ? PWA_BANNER_LIFT_PX : 0;
  const rightPx = agendaFab ? 16 + AGENDA_FAB_WIDTH_PX + FAB_GAP_PX : 16;

  return (
    <>
      <motion.button
        type="button"
        onClick={() => setOpen((s) => !s)}
        whileTap={{ scale: 0.92 }}
        data-testid="global-plus-btn"
        aria-label={tr("Crear")}
        className="fixed z-40 w-12 h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl flex items-center justify-center shadow-gold transition-colors"
        style={{ boxShadow: '0 10px 28px rgba(245, 197, 24, 0.35)', bottom: `calc(6rem + ${lift}px)`, right: rightPx }}
      >
        <motion.div animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.18 }}>
          <Plus className="w-6 h-6" strokeWidth={2.5} />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
            onClick={close}
            className="fixed inset-0 bg-background/70 backdrop-blur-sm z-40 flex items-end justify-end p-4"
            style={{ paddingBottom: 160 + lift }}
            data-testid="global-plus-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-80 max-w-[calc(100vw-2rem)] bg-card border-2 border-primary/30 rounded-2xl shadow-2xl overflow-hidden"
              data-testid="global-plus-menu"
            >
              <div className="p-3 border-b border-border flex items-center justify-between">
                <h3 className="font-display text-sm font-bold text-foreground">{tr("Crear nuevo")}</h3>
                <button type="button" onClick={close} className="p-1 rounded-full hover:bg-secondary" data-testid="global-plus-close">
                  <X className="w-4 h-4 text-muted-foreground pointer-events-none" />
                </button>
              </div>
              <div className="p-2 space-y-2 max-h-[62vh] overflow-y-auto" data-testid="global-plus-options">
                {sections.map((s) => (
                  <div key={s.id} data-testid={`create-section-${s.id}`}>
                    {s.title && <p className="px-3 pt-1 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{s.title}</p>}
                    <div className="space-y-0.5">
                      {s.items.map((opt) => <MenuItem key={opt.id} opt={opt} />)}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <UploadContentDialog open={postOpen} onOpenChange={setPostOpen} />
      <StoryUploadDialog open={storyOpen} onOpenChange={setStoryOpen} />
      {highlightOpen && (
        <HighlightEditor open mode="create" barber={user} onOpenChange={(o) => { if (!o) setHighlightOpen(false); }}
          onSaved={() => { setHighlightOpen(false); toast.success(tr("Destacada creada")); navigate(user.role === 'barbershop' ? `/shop/${user.username}` : '/profile'); }} />
      )}
      <RequestReviewDialog open={reviewLinkOpen} onOpenChange={setReviewLinkOpen} />
      <FeedPostUploader open={Boolean(feedDestination)} onOpenChange={(o) => { if (!o) setFeedDestination(null); }} preselectedDestination={feedDestination} />
    </>
  );
};

const MenuItem = ({ opt }) => {
  const Icon = opt.icon;
  const soon = Boolean(opt.soon);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault(); e.stopPropagation();
        if (soon) { toast.info(tr("Próximamente")); return; }
        opt.run?.();
      }}
      disabled={soon}
      aria-disabled={soon ? 'true' : 'false'}
      data-testid={`create-opt-${opt.id}`}
      className={`relative w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors ${
        soon ? 'opacity-50 cursor-not-allowed' : opt.primary ? 'bg-primary/10 border border-primary/40 hover:bg-primary/15' : 'hover:bg-primary/10'
      }`}
    >
      <div className={`relative w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${soon ? 'bg-muted/40 border border-border/60' : 'bg-primary/15 border border-primary/30'}`}>
        {soon ? <Lock className="w-4 h-4 text-muted-foreground" /> : <Icon className="w-4 h-4 text-primary" />}
        {opt.accent === 'live' && !soon && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#EF4444] ring-2 ring-card animate-pulse" aria-hidden="true" data-testid={`create-opt-${opt.id}-live-dot`} />
        )}
      </div>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-sm leading-tight">{opt.label}</span>
        {opt.hint && !soon && <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">{opt.hint}</span>}
      </span>
      {soon && (
        <span className="shrink-0 whitespace-nowrap text-[9px] font-bold uppercase tracking-wide text-muted-foreground border border-border rounded-full px-2 py-0.5" data-testid={`create-opt-${opt.id}-soon`}>{tr("Próximamente")}</span>
      )}
    </button>
  );
};
